import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Ask Atom — scripted chat. Secondary location: a small floating button (FAB)
 * in the bottom-right of the phone shell. Mostly Atom acts before being asked;
 * chat is a fallback capability, not the main event.
 */
export default function GDLAskAtomFab({ csId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || !csId) return;
    axios.get(`${API}/api/demo/gdl/atom/chat/${csId}/history`,
      { withCredentials: true }).then(r => setHistory(r.data));
  }, [open, csId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history.length]);

  const send = async () => {
    if (!draft.trim() || busy || !csId) return;
    setBusy(true);
    const userText = draft.trim();
    setDraft('');
    setHistory(h => [...h, { role: 'user', text: userText }]);
    try {
      const { data } = await axios.post(`${API}/api/demo/gdl/atom/chat/${csId}`,
        { text: userText }, { withCredentials: true });
      setHistory(h => [...h, data]);
    } catch (e) {
      setHistory(h => [...h, { role: 'atom', text: 'Atom is offline right now.' }]);
    } finally { setBusy(false); }
  };

  if (!csId) return null;
  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ask-atom-fab" style={{
          position: 'absolute', right: 14, bottom: 90, zIndex: 30,
          width: 48, height: 48, borderRadius: '50%',
          background: 'linear-gradient(135deg, #B03140, #8E2233)',
          border: '2px solid #fff',
          boxShadow: '0 4px 14px rgba(176,49,64,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}>
          <MessageCircle size={20} color="#fff" />
        </button>
      )}

      {open && (
        <div data-testid="ask-atom-panel" style={{
          position: 'absolute', left: 14, right: 14, bottom: 86, top: 80,
          background: '#fff', borderRadius: 18,
          boxShadow: '0 12px 40px rgba(13,3,5,0.4)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zIndex: 35, border: '1px solid rgba(176,49,64,0.15)',
        }}>
          <div style={{
            padding: '12px 16px', background: 'linear-gradient(135deg, #B03140, #8E2233)',
            color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageCircle size={14} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Ask Atom</div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>Financial copilot</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ask-atom-close"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 12, background: 'rgba(176,49,64,0.02)' }}>
            {history.length === 0 && (
              <div style={{ color: '#A07078', fontSize: 12, textAlign: 'center', padding: 20 }}>
                Ask about your balance, loan, deductions, savings, investments,
                stocks — or say "help" for a human.
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}-${i}`} style={{
                display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 8,
              }}>
                <div style={{
                  maxWidth: '78%', padding: '8px 12px', borderRadius: 14,
                  fontSize: 12, lineHeight: 1.45,
                  background: m.role === 'user' ? '#B03140' : '#fff',
                  color: m.role === 'user' ? '#fff' : '#2D0A10',
                  border: m.role === 'atom' ? '1px solid rgba(176,49,64,0.1)' : 'none',
                }}>
                  {m.text}
                  {m.particle_tag && (
                    <div style={{ marginTop: 6, display: 'inline-block',
                      padding: '2px 8px', borderRadius: 999, fontSize: 9,
                      fontWeight: 700, background: 'rgba(176,49,64,0.08)',
                      color: '#8E2233' }}>
                      {m.particle_tag}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div style={{ padding: 10, borderTop: '1px solid #F0E5E7', display: 'flex', gap: 6 }}>
            <input value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Ask Atom..." data-testid="ask-atom-input"
              style={{ flex: 1, padding: '8px 12px', fontSize: 12,
                border: '1px solid #E5E7EB', borderRadius: 999 }} />
            <button onClick={send} data-testid="ask-atom-send" disabled={busy}
              style={{ width: 34, height: 34, borderRadius: '50%',
                background: '#B03140', color: '#fff', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
